<script>
/* eslint-disable id-length */
import { isNil } from 'lodash-es';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

/**
 * Our Job Sites map was very unreliable and unresponsive whenever we needed to load large amounts of
 * job sites (we have customers with over 3000). This component leverages the library `@googlemaps/markerclusterer`
 * and groups markers together, deferring the rendering logic directly to the google maps library without involving
 * Vue in the rendering process.
 *
 * With this renderless component approach we still have most of the features that we like about Vue like reactivity and declarativeness.
 * The solution that we propose here is divided into:
 * - Accept the complete list of markers as a prop
 * - Create an actual Google Maps Marker for each one of them
 * - Create the Clusterer instance
 * - Upon map tiles loaded (proper initialization):
 *  - Compute the markers that are within the bounds of the current view in the map
 *  - Add a click listener to each of them so that we can react and `$emit` the proper events
 *  - Add them to the Clusterer for rendering
 *
 * Because we have some functionality that depends on `click` or `hover` interactions,
 * we have an internal `state` that helps track which marker are we dealing with,
 * whether we're hovering it or clicking it, etc. So that the parent can react
 * accordingly to the events.
 *
 * NOTE: For usage, please see the `TheJobSites.vue` component.
 */
export default {
  name: 'AppMapCluster',
  props: {
    map: {
      type: Object,
      default: null,
    },
    // Markers to be rendered and processed by the clusterer.
    // NOTE: These are not the actual "Google Maps Markers", but
    // rather the entities that we need to paint on the map.
    // e.g.: Job sites
    markers: {
      type: Array,
      required: true,
    },
    markerSize: {
      type: Object,
      required: true,
    },
    // This is a possible marker that might be selected from the outside world.
    // e.g: Job sites sidebar component, this will help us sync both worlds.
    externalSelectedMarkerId: {
      type: Number,
      required: false,
    },
  },
  emits: ['marker-click'],
  data: () => ({
    selectedMarker: null,
    markerState: {
      // whether we're hovering or not
      hover: false,
      // Whether or not, we're currently selecting a marker
      selected: false,
      // The original marker object (passed as props)
      marker: null,
    },
  }),
  computed: {
    mapReady() {
      // Read the reactive `map` prop FIRST: `google` is plain `window.google` (not reactive), so if
      // it short-circuits the check while the Maps script is still loading, this computed tracks no
      // reactive dependency and never re-evaluates — the watcher below then never initializes the
      // clusterer and no pins render. `map` only becomes non-nil after the API has loaded, so
      // reading it first both tracks it and guarantees `window.google` exists by then.
      return !isNil(this.map) && !isNil(this.google);
    },
    google() {
      return window.google;
    },
  },
  watch: {
    // With this watcher, we check if we have a marker selected from the outside world.
    // e.g: Job sites sidebar component.
    // If so, we'll trigger a click event on the actual Google Maps Marker.
    async externalSelectedMarkerId(newId, oldId) {
      await this.$nextTick();

      if (newId) {
        const [, marker] = this.allMarkers.get(newId);

        if (marker) {
          await this.$nextTick();
          this.google.maps.event.trigger(marker, 'click');
        }
      } else if (oldId && !newId) {
        // We deselected something
        this.deselectMarker();
      }
    },
    mapReady(isReady) {
      if (!isReady) {
        return;
      }

      this.initializeClusterer();
    },
    markers(markers, oldMarkers) {
      // This is kinda of a naive approach to the problem
      // Because what would happen if in the map page we get
      // some new data from the backend for sites, this wouldn't
      // reflect the changes in the cluster.
      if (!this.mapReady || !this.map || !this.map.getBounds()) {
        return;
      }

      this.updateClusterer();

      // Make sure that we update the selected marker, if any.
      if (this.markerState.marker && this.allMarkers.has(this.markerState.marker.id)) {
        // We have a marker selected, and we might be showing the info
        // window, so update the `markerState` to reflect it.
        const [markerObject] = this.allMarkers.get(this.markerState.marker.id);

        this.markerState.marker = markerObject;
      }
    },
  },
  created() {
    // Non-reactive instance state (kept off `data()`): the google.maps.Marker / MarkerClusterer
    // instances must stay RAW. In Vue 3 a reactive Map deep-wraps its values in Proxies, and
    // @googlemaps/markerclusterer's deep-equality then walks the proxied Maps object graph
    // endlessly ("Maximum call stack size exceeded"). None of this feeds the template.
    this.clusterer = null;
    this.allMarkers = new Map();
    this.mapListeners = [];
    this.markerListeners = new Set();
  },
  beforeUnmount() {
    this.cleanupMarkerListeners();
    this.mapListeners.forEach((listenerId) => {
      this.google.maps.event.removeListener(listenerId);
    });
  },
  methods: {
    cleanupMarkerListeners() {
      for (const listenerId of this.markerListeners) {
        this.google.maps.event.removeListener(listenerId);
        this.markerListeners.delete(listenerId);
      }
    },
    createClusterer() {
      this.clusterer = new MarkerClusterer({
        // Initialize the markers as empty array as we will compute them
        // later based on the map's boundaries.
        markers: [],
        map: this.map,
      });
    },
    createAllMarkers() {
      // For event emitting purposes, make sure that we have a reference to
      // the original `$props.markers` so that we can send the proper
      // payload to the event listeners.
      const allMarkers = new Map();

      for (const markerObject of this.markers) {
        allMarkers.set(markerObject.id, [
          markerObject,
          new this.google.maps.Marker({
            clickable: true,
            position: {
              lat: parseFloat(markerObject.latitude || markerObject.position.lat),
              lng: parseFloat(markerObject.longitude || markerObject.position.lng),
            },
          }),
        ]);
      }

      this.allMarkers.clear();
      this.allMarkers = allMarkers;
    },
    renderClusterer() {
      // Make sure that we only track those markers that are within the map's view bounds
      const visibleMarkers = [];
      const hoverHandler = (hover, markerObject) => () => {
        this.markerState = this.markerState.selected
          ? this.markerState
          : { hover, selected: false, marker: markerObject };
      };

      for (const [markerObject, marker] of this.allMarkers.values()) {
        if (this.map.getBounds().contains(marker.getPosition())) {
          this.markerListeners.add(
            marker.addListener('click', () => {
              this.markerState = { hover: false, selected: true, marker: markerObject };
              this.$emit('marker-click', markerObject);
            }),
          );
          this.markerListeners.add(marker.addListener('mouseover', hoverHandler(true, markerObject)));
          this.markerListeners.add(marker.addListener('mouseout', hoverHandler(false, null)));

          visibleMarkers.push(marker);
          marker.setIcon(this.getIcon(markerObject.icon));
        }
      }

      // Cleanup any previous visible markers in the clusterer
      this.clusterer.clearMarkers(true);
      // Pass `false` for `noDraw` explicitly.
      // If we were to pass `true` we wouldn't see anything
      this.clusterer.addMarkers(visibleMarkers, false);
    },
    initializeClusterer() {
      // Make sure that we don't have hanging listeners
      this.cleanupMarkerListeners();
      this.createAllMarkers();
      this.createClusterer();

      this.mapListeners.push(
        // Prepare the map the every time the map tiles are loaded
        // We re-render the clusterer by just rendering the visible markers within bounds.
        // For more info: https://developers.google.com/maps/documentation/javascript/events#EventsOverview
        this.google.maps.event.addListener(this.map, 'tilesloaded', () => this.renderClusterer()),
        // Event listener, to clear the map whenever the user clicks the map directly
        // This won't interfere with the other `click` events at the Vue side.
        this.google.maps.event.addListener(this.map, 'click', () => this.deselectMarker()),
      );
    },
    updateClusterer() {
      this.cleanupMarkerListeners();
      this.createAllMarkers();
      this.renderClusterer();
    },

    // Helpers
    getIcon(url) {
      const { width, height } = this.markerSize;
      const anchorY = width === height ? height / 2 : height;

      return {
        url,
        size: { width, height, f: 'px', b: 'px' },
        scaledSize: { width, height, f: 'px', b: 'px' },
        anchor: { x: parseInt(width / 2), y: parseInt(anchorY) },
      };
    },
    // This will be passed in the scoped slot to make sure that the parent
    // deselects can deselect the marker in the cluster, and thus preventing
    // to have an ever open info window for the different markers.
    deselectMarker() {
      this.markerState = { hover: false, selected: false, marker: null };
    },
  },
  render() {
    if (this.$slots?.default) {
      return this.$slots.default({
        state: this.markerState,
        deselect: this.deselectMarker,
      });
    }

    return null;
  },
};
</script>
